import {
	Box,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	LinearProgress,
	Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import * as React from 'react'
import { getIndirectBucketContributors } from '../../../services/shiftEndReportService'
import {
	BucketContributors,
	ContributorNode,
	TaskBucket,
} from '../../../types/Reports/ShiftEnd/TimeSheet'
import { JobRoleTitles } from '../../../types/Employee/JobRole'

type ContributorsDialogProps = {
	open: boolean
	onClose: () => void
	employeeId: number
	employeeName: string
	shiftDate: Date
	bucket: TaskBucket | null
}

function formatHours(hours: number): string {
	return `${hours.toFixed(2)} h`
}

function roleLabel(roleTitle: string): string {
	const key = roleTitle as keyof typeof JobRoleTitles
	return JobRoleTitles[key] ?? roleTitle
}

function ContributorTreeNode({
	node,
	totalHours,
	depth,
}: {
	node: ContributorNode
	totalHours: number
	depth: number
}) {
	const pct = totalHours > 0 ? Math.min(100, (node.hours / totalHours) * 100) : 0

	return (
		<Box sx={{ ml: depth === 0 ? 0 : 2, mt: depth === 0 ? 0 : 1 }}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 1,
					mb: 0.5,
				}}
			>
				<Box sx={{ minWidth: 0 }}>
					<Typography variant='body2' sx={{ fontWeight: depth === 0 ? 600 : 500 }} noWrap>
						{node.name}
					</Typography>
					<Typography variant='caption' color='text.secondary'>
						{roleLabel(node.roleTitle)}
					</Typography>
				</Box>
				<Typography variant='body2' sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
					{formatHours(node.hours)}
				</Typography>
			</Box>
			<LinearProgress
				variant='determinate'
				value={pct}
				sx={{
					height: 8,
					borderRadius: 1,
					mb: 0.5,
					bgcolor: 'action.hover',
					'& .MuiLinearProgress-bar': {
						borderRadius: 1,
						bgcolor: depth === 0 ? 'success.main' : depth === 1 ? 'info.main' : 'warning.main',
					},
				}}
			/>
			{node.children?.map((child) => (
				<ContributorTreeNode
					key={child.workerId}
					node={child}
					totalHours={totalHours}
					depth={depth + 1}
				/>
			))}
		</Box>
	)
}

export function ContributorsDialog({
	open,
	onClose,
	employeeId,
	employeeName,
	shiftDate,
	bucket,
}: ContributorsDialogProps) {
	const [loading, setLoading] = React.useState(false)
	const [data, setData] = React.useState<BucketContributors | null>(null)

	React.useEffect(() => {
		if (!open || !bucket) {
			setData(null)
			return
		}

		let cancelled = false
		setLoading(true)
		getIndirectBucketContributors(employeeId, shiftDate, {
			workItemTaskId: bucket.workItemTaskId,
			reworkId: bucket.reworkId,
			constraintId: bucket.constraintId,
			nProgressibleTypeId: bucket.nProgressibleTypeId,
			activity: bucket.activity,
			type: bucket.type,
		})
			.then((result) => {
				if (!cancelled) setData(result ?? { roots: [], totalHours: 0 })
			})
			.catch(console.error)
			.finally(() => {
				if (!cancelled) setLoading(false)
			})

		return () => {
			cancelled = true
		}
	}, [open, bucket, employeeId, shiftDate])

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
			<DialogTitle sx={{ pr: 6 }}>
				Contributors
				{bucket && (
					<Typography variant='body2' color='text.secondary' component='div'>
						{bucket.activity}
						{employeeName ? ` · ${employeeName}` : ''}
					</Typography>
				)}
			</DialogTitle>
			<IconButton
				onClick={onClose}
				sx={(theme) => ({
					position: 'absolute',
					right: 8,
					top: 8,
					color: theme.palette.grey[500],
				})}
			>
				<CloseIcon />
			</IconButton>
			<DialogContent dividers>
				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
						<CircularProgress size={28} color='success' />
					</Box>
				) : !data || data.roots.length === 0 ? (
					<Typography variant='body2' color='text.secondary'>
						No contributors found for this activity.
					</Typography>
				) : (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
						<Typography variant='caption' color='text.secondary'>
							Crew hours for this activity (total {formatHours(data.totalHours)})
						</Typography>
						{data.roots.map((root) => (
							<ContributorTreeNode
								key={root.workerId}
								node={root}
								totalHours={data.totalHours}
								depth={0}
							/>
						))}
					</Box>
				)}
			</DialogContent>
		</Dialog>
	)
}
